import React, { useEffect, useState } from "react";
import { Users, Copy, Check, Share2, TrendingUp, Wallet, UserPlus } from "lucide-react";
import { useApp } from "../context/AppContext";
import { affiliateApi } from "../lib/api";
import { inr } from "../lib/format";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";

const Stat = ({ label, value, cls = "text-primary" }) => (
  <div className="rounded-2xl border border-border bg-card p-4">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className={`mt-1 font-display text-2xl font-bold ${cls}`}>{value}</div>
  </div>
);

export default function Affiliate() {
  const { isAuthed } = useApp();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = () => affiliateApi.get().then(setData).catch(() => {});
  useEffect(() => { if (isAuthed) load(); }, [isAuthed]);

  const create = async () => {
    setBusy(true);
    try { await affiliateApi.create(); await load(); toast({ title: "Affiliate account created 🎉" }); }
    catch (e) { toast({ title: "Failed", description: e?.response?.data?.detail || "Try again." }); }
    finally { setBusy(false); }
  };

  const link = data?.affiliateCode ? `https://batballworld.com/?aff=${data.affiliateCode}` : "";
  const copy = () => {
    if (!data?.affiliateCode) return;
    navigator.clipboard.writeText(`Join BatBallWorld 🏏 with my affiliate code ${data.affiliateCode} — ${link}`);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
    toast({ title: "Affiliate link copied!" });
  };
  const share = async () => {
    if (!link) return;
    const text = `Join BatBallWorld 🏏 with my affiliate code ${data.affiliateCode} — ${link}`;
    if (navigator.share) { try { await navigator.share({ title: "BatBallWorld Affiliate", text, url: link }); } catch { /* no-op */ } }
    else copy();
  };

  if (!isAuthed) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="rounded-2xl border border-border bg-card p-8 text-center text-muted-foreground" data-testid="affiliate-signin">
          Sign in to open your affiliate account.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="affiliate-page">
      <div className="flex items-center gap-3 mb-2">
        <span className="h-11 w-11 rounded-xl bg-teal-500/15 grid place-items-center ring-1 ring-teal-500/30 shrink-0">
          <Users className="h-5 w-5 text-teal-400" />
        </span>
        <div>
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl">Affiliate Account</h1>
          <p className="text-sm text-muted-foreground">Build your team and earn {data?.pct ?? 10}% of their losses.</p>
        </div>
      </div>

      {/* Not yet an affiliate */}
      {data && !data.active && (
        <div className="mt-6 rounded-2xl border border-teal-500/25 bg-teal-500/5 p-6 text-center" data-testid="affiliate-create-card">
          <UserPlus className="h-10 w-10 text-teal-400 mx-auto" />
          <h2 className="mt-3 font-display font-bold text-xl">Create your affiliate account</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            Get your own affiliate code, invite members, and earn {data.pct}% of the total net losses of the members under you.
          </p>
          <Button onClick={create} disabled={busy} className="mt-4 font-semibold bg-teal-500 hover:bg-teal-400 text-black" data-testid="affiliate-create-btn">
            {busy ? "Creating…" : "Create Affiliate Account"}
          </Button>
        </div>
      )}

      {/* Active affiliate dashboard */}
      {data && data.active && (
        <>
          <div className="mt-6 rounded-2xl border border-teal-500/25 bg-teal-500/5 p-5" data-testid="affiliate-code-card">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Your affiliate code</div>
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 rounded-lg bg-secondary px-3 py-2 font-display font-bold tracking-wider text-lg" data-testid="affiliate-code">{data.affiliateCode}</code>
              <Button size="icon" variant="outline" className="bg-white/5 border-white/15" onClick={copy} data-testid="affiliate-copy-btn">
                {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <div className="mt-2 rounded-lg bg-black/20 px-3 py-2 text-[11px] text-muted-foreground break-all" data-testid="affiliate-link">{link}</div>
            <Button className="mt-3 w-full font-semibold bg-teal-500 hover:bg-teal-400 text-black" onClick={share} data-testid="affiliate-share-btn">
              <Share2 className="h-4 w-4 mr-1.5" /> Share link &amp; code
            </Button>
          </div>

          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Members" value={data.totalMembers} cls="text-foreground" />
            <Stat label={`Counted (÷${data.block})`} value={data.countedMembers} cls="text-teal-400" />
            <Stat label="Pending payout" value={inr(data.pending)} />
            <Stat label="Paid out" value={inr(data.paid)} cls="text-foreground" />
          </div>

          <div className="mt-4 rounded-2xl border border-border bg-card p-4" data-testid="affiliate-earnings">
            <div className="flex items-center gap-2 text-sm">
              <TrendingUp className="h-4 w-4 text-teal-400" />
              <span className="text-muted-foreground">Total commission ({data.pct}% of counted losses)</span>
            </div>
            <div className="mt-1 font-display text-3xl font-bold text-primary">{inr(data.commission)}</div>
            {!data.eligible && (
              <p className="mt-2 text-xs text-amber-400">Reach {data.minMembers} members to unlock payouts. Members are counted in blocks of {data.block}.</p>
            )}
            <p className="mt-2 text-[11px] text-muted-foreground flex items-center gap-1"><Wallet className="h-3 w-3" /> Payouts are approved and credited by our team.</p>
          </div>

          <h3 className="font-display font-semibold text-lg mt-6 mb-3">Your members</h3>
          <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="affiliate-members">
            <div className="grid grid-cols-[1fr_auto] gap-2 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border">
              <span>Member</span><span className="text-right">Counted</span>
            </div>
            {data.members.length === 0 && <div className="px-4 py-8 text-center text-muted-foreground" data-testid="affiliate-empty">No members yet. Share your code to grow your team.</div>}
            {data.members.map((m, i) => (
              <div key={i} className="grid grid-cols-[1fr_auto] gap-2 px-4 py-3 text-sm border-b border-border/50 last:border-0" data-testid={`affiliate-member-${i}`}>
                <span className="font-medium truncate">{m.name}</span>
                <span className="text-right">
                  {m.counted
                    ? <span className="rounded-full bg-teal-500/15 text-teal-400 text-[10px] font-bold px-2 py-0.5">Counted</span>
                    : <span className="rounded-full bg-white/5 text-muted-foreground text-[10px] font-bold px-2 py-0.5">—</span>}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-5 rounded-2xl bg-black/20 border border-border/60 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">How it works</div>
            <ul className="space-y-1.5 text-sm text-muted-foreground list-disc pl-4">
              <li>Share your affiliate code — anyone who signs up with it joins your team.</li>
              <li>You need at least {data.minMembers} members; members count only in complete blocks of {data.block} (5, 10, 15…).</li>
              <li>You earn {data.pct}% of the total net losses of your counted members.</li>
              <li>Members under an affiliate are not eligible for the Losing Bonus.</li>
              <li>Commission is reviewed and paid to your wallet by our team.</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
